import { DateTime } from "luxon";
import { backendService } from "../../services/backend.service";
import { formatPersonFullName } from "../../utils/formatters";
import { EmploymentType, type Nurse, type NurseInShift, NurseTrainingStatus } from "../../utils/models";

/**
 * Fetches PTO-deleted shifts from the API and maps them to frontend NurseInShift objects.
 *
 * Throws on failure rather than returning `[]`: swallowing it silently dropped the PTO sheet from an
 * export the user believed contained it. The caller decides how to surface that.
 */
export const fetchPtoDeletedShifts = async (
	departmentId: string,
	startDate: string,
	endDate: string,
	nurses: Nurse[],
	lastNameFirst: boolean,
): Promise<NurseInShift[]> => {
	const response = await backendService.getPtoDeletedShifts(departmentId, startDate, endDate);
	const result: NurseInShift[] = [];
	for (const shift of response.data) {
		const fullNurse = nurses.find((n) => n.id === shift.nurseId);
		if (!fullNurse) continue;
		const rawShift = shift as { startTime?: string; endTime?: string; shiftType?: string; shift_type?: string; start_time?: string; end_time?: string };
		const startIso = rawShift.startTime ?? rawShift.start_time ?? "";
		const endIso = rawShift.endTime ?? rawShift.end_time ?? "";
		result.push({
			id: "",
			nurseId: shift.nurseId,
			departmentId: shift.departmentId,
			startTime: DateTime.fromISO(startIso),
			endTime: DateTime.fromISO(endIso),
			isPending: false,
			isPendingToDelete: false,
			location: shift.location ?? "",
			role: fullNurse.role,
			charge: shift.charge ?? false,
			name: formatPersonFullName(fullNurse, lastNameFirst),
			type: fullNurse.employmentType ?? EmploymentType.FullTime,
			shiftType: rawShift.shiftType ?? rawShift.shift_type ?? "",
			bonus: 0,
			notes: "",
			breaks: [],
			trainingStatus: (shift.trainingStatus as NurseTrainingStatus) ?? NurseTrainingStatus.Regular,
			message: "PTO Removal",
		});
	}
	return result;
};

/**
 * Trigger a browser download for a Blob that already carries its own MIME type.
 *
 * Distinct from `downloads/utils.handleDownload`, which re-wraps its input in a new Blob with a
 * caller-supplied (xlsx-by-default) type — repointing these callers at it would silently re-type
 * their CSV/JSON payloads. Consolidating the two is a follow-up that has to fix the types per site.
 */
export const downloadBlob = (blob: Blob, filename: string) => {
	const url = URL.createObjectURL(blob);
	const link = document.createElement("a");
	link.href = url;
	link.download = filename;
	document.body.appendChild(link);
	link.click();
	document.body.removeChild(link);
	URL.revokeObjectURL(url);
};
